'use client';

import * as React from 'react';

/*
 * Lets an open overlay answer the system back button (Android back, iOS edge
 * swipe) without this package knowing how the host app navigates. The host
 * mounts <BackLayerProvider>; without one these hooks do nothing.
 */

interface BackLayerOptions {
  /** Bind on desktop too. Default: phones and touch devices only. */
  always?: boolean;
  /** URL for the layer's own history entry (default: the URL it opens on). */
  path?: string;
}

interface BackLayerHandle {
  /** The layer closed by something other than the back button. */
  release: () => void;
  /** Leave the layer the way the back button would; false if it cannot. */
  stepBack: () => boolean;
}

export interface BackLayerRegistry {
  register: (onBack: () => void, options?: BackLayerOptions) => BackLayerHandle;
}

const BackLayerContext = React.createContext<BackLayerRegistry | null>(null);

export const BackLayerProvider = BackLayerContext.Provider;

/**
 * While `open`, the back button calls `onBack`, which must close the layer.
 * Returns `stepBack` for the layer's own back arrow.
 */
export function useBackLayer(
  open: boolean,
  onBack: () => void,
  options?: BackLayerOptions,
) {
  const registry = React.useContext(BackLayerContext);
  const latest = React.useRef({ onBack, options });
  React.useEffect(() => {
    latest.current = { onBack, options };
  });

  const handle = React.useRef<BackLayerHandle | null>(null);
  React.useEffect(() => {
    if (!open || !registry) return;
    // Options are read once, as the layer opens.
    handle.current = registry.register(
      () => latest.current.onBack(),
      latest.current.options,
    );
    return () => {
      handle.current?.release();
      handle.current = null;
    };
  }, [open, registry]);

  return React.useCallback(() => handle.current?.stepBack() ?? false, []);
}

interface OpenProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** `open` / `onOpenChange` for a Radix Root; Back closes it as an outside tap would. */
export function useBackDismissibleOpen(
  { open: controlled, defaultOpen, onOpenChange }: OpenProps,
  bound = true,
) {
  const [uncontrolled, setUncontrolled] = React.useState(defaultOpen ?? false);
  const open = controlled ?? uncontrolled;
  const setOpen = (next: boolean) => {
    if (controlled === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  };
  useBackLayer(open && bound, () => setOpen(false));
  return { open, onOpenChange: setOpen };
}
